import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, Trash2, Car, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useWorkspace } from "../../store/WorkspaceContext";

const API = process.env.REACT_APP_BACKEND_URL;
const SIM_HIDDEN_KEY = "xpattern.simHidden";

// The customer's own vehicle photos — upload one (background removed, body + glass traced), click it to see wraps and decals on it.
export const MyVehiclesPanel = () => {
  const { state, dispatch } = useWorkspace();
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const load = () => api.userVehicles().then(setMine).catch(() => setMine([]));
  useEffect(() => { load(); }, [state.preview?.body]);
  const current = state.preview?.body;
  const show = (id) => { localStorage.setItem(SIM_HIDDEN_KEY, ""); dispatch({ type: "SET", patch: { preview: { ...(state.preview || {}), body: `u:${id}`, view: "side", before: false } } }); };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await api.userVehicleUpload(file, file.name.replace(/\.[^.]+$/, ""), 190);
      await load();
      show(r.id);
      if (r.warning) toast.warning(r.warning); else toast.success(r.cutout ? "Your vehicle is ready — background removed, pick a wrap color or drop a graphic on it" : "Your vehicle is ready — pick a wrap color or drop a graphic on it");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not read that photo"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const remove = async (v) => {
    if (!window.confirm(`Remove “${v.name}” from your vehicles?`)) return;
    try {
      await api.userVehicleDelete(v.id);
      if (current === `u:${v.id}`) dispatch({ type: "SET", patch: { preview: { ...(state.preview || {}), body: "rollsroyce", view: "side" } } });
      load();
    } catch { toast.error("Could not remove that photo"); }
  };
  return (
    <div className="border-b border-[#27324a]" data-testid="my-vehicles-panel">
      <div className="flex items-center justify-between gap-2 px-3 pt-2 pb-1">
        <span className="flex items-center gap-1.5 text-[12px] font-semibold text-[#e8e9ec]"><Car className="h-3.5 w-3.5 text-[#c084fc]" /> My vehicles <span className="font-mono text-[11px] font-normal text-[#6b7079]" data-testid="my-vehicles-count">{mine ? mine.length : "…"}</span></span>
        <button data-testid="my-vehicle-upload-btn" className="btn h-9 gap-1.5 px-3 text-[13px] font-semibold" disabled={busy} onClick={() => inputRef.current?.click()}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} {busy ? "Reading photo…" : "Upload vehicle"}</button>
        <input ref={inputRef} data-testid="my-vehicle-upload-input" type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      </div>
      {mine && !mine.length && (
        <div data-testid="my-vehicles-empty" onClick={() => !busy && inputRef.current?.click()} className="m-3 mt-1 flex cursor-pointer flex-col items-center gap-1.5 rounded-xl border-2 border-dashed border-[#354260] bg-[#0c1017] p-4 text-center hover:border-[#c084fc]">
          <Camera className="h-6 w-6 text-[#c084fc]" /><span className="text-[12px] text-[#e8e9ec]">No vehicle photos yet — upload a picture of your car</span><span className="text-[10px] text-[#6b7079]">A clear side-on photo in daylight works best · we cut the background away so only your vehicle shows</span>
        </div>
      )}
      {mine?.length > 0 && (
        <div className="flex gap-2 overflow-x-auto px-3 pb-2" data-testid="my-vehicles-list">
          {mine.map((v) => {
            const on = current === `u:${v.id}`;
            return (
              <div key={v.id} data-testid={`my-vehicle-${v.id}`} onClick={() => show(v.id)} title={on ? "Shown on the preview" : `Show ${v.name} on the preview`}
                className={`group relative w-[108px] flex-none cursor-pointer rounded-lg border p-1 transition-colors ${on ? "border-[#c084fc] bg-[#c084fc]/15" : "border-[#27324a] bg-[#0c1017] hover:border-[#4c9ffe]"}`}>
                <div className="flex h-[58px] items-center justify-center overflow-hidden rounded bg-[#141a24]"><img src={`${API}${v.url}`} alt={v.name} className="max-h-full max-w-full object-contain" /></div>
                <div className="mt-1 flex items-center gap-1">
                  {on && <Check className="h-3 w-3 flex-none text-[#c084fc]" />}
                  <span className="min-w-0 flex-1 truncate text-[11px] text-[#e8e9ec]">{v.name}</span>
                  <button data-testid={`my-vehicle-delete-${v.id}`} className="flex-none rounded p-0.5 text-[#6b7079] opacity-0 transition-opacity hover:text-[#e5484d] group-hover:opacity-100" title="Remove this photo" onClick={(e) => { e.stopPropagation(); remove(v); }}><Trash2 className="h-3 w-3" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
